import React, { useMemo, useContext, useEffect } from 'react';
import { IClientAssociationModel } from '../../../../../../Types/ClientModelTypes';
import { IMasterCompProps } from '../../../../../../Types/MasterDetailTypes';
import {
  UpdateAssociation,
  IAssociationObjectListInfo,
} from './AssociationObjectList';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { makeStyles } from 'tss-react/mui';
import { BreadcrumbsContext } from '../../RutinBreadcrumbs/BreadcrumbsContext';
import { nth } from 'ramda';
import { createPortal } from 'react-dom';
import BusinessDataObjectDetailHeader from '../BusinessDataObjectDetailHeader/BusinessDataObjectDetailHeader';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { BusinessDataFormContext } from '../BusinessDataForm/BusinessDataForm';

interface IInnerProps extends IMasterCompProps {
  associationObjects: IClientAssociationModel[];
  updateAssociation: UpdateAssociation;
  data: INodeData;
}

const useStyles = makeStyles()(
  theme =>
    ({
      text: {
        color: theme.palette.primary.main,
        cursor: 'pointer',
        '&:hover': {
          borderBottom: `2px solid ${theme.palette.primary.light}`,
          marginBottom: -2,
        },
      },
      textSpacing: {
        padding: '4px 0px',
      },
    } as const)
);

const AssociationObjectListMaster = (props: IInnerProps) => {
  const { selectDetail, updateAssociation } = props;
  const { classes, cx } = useStyles();
  return (
    <>
      {useMemo(() => {
        return (
          <div>
            {props.associationObjects.map((x, index) => {
              const association = props.data._Associations
                ? props.data._Associations[x.AssociationName]
                : null;
              const id = association?.id;
              return (
                <EDTypography
                  key={index}
                  variant="h6"
                  className={cx(classes.textSpacing, {
                    [classes.text]: id,
                  })}
                  onClick={
                    id
                      ? () => updateAssociation(id, x, selectDetail)
                      : undefined
                  }
                >
                  {x.DisplayLabel}
                </EDTypography>
              );
            })}
          </div>
        );
      }, [
        classes.text,
        classes.textSpacing,
        props.associationObjects,
        props.data._Associations,
        selectDetail,
        updateAssociation,
        cx,
      ])}
    </>
  );
};

const MasterComp = (
  associationObjects: IClientAssociationModel[],
  updateAssociation: UpdateAssociation,
  unselectAssociation: () => void,
  data: INodeData,
  association: IAssociationObjectListInfo | null
): React.ComponentType<React.PropsWithChildren<IMasterCompProps>> => {
  const formContext = useContext(BusinessDataFormContext);
  return (props: IMasterCompProps) => {
    const {
      headerReferences,
      currentNavLevel,
      topNavLevel,
      backButtonReferences,
      registerActiveNavLevel,
      unregisterActiveNavLevel,
      // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useContext(BreadcrumbsContext);
    const headerRef =
      props.selectedDetail && currentNavLevel !== undefined
        ? headerReferences[currentNavLevel]
        : undefined;
    const renderHeader = headerRef != null;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (renderHeader && currentNavLevel) {
        registerActiveNavLevel(currentNavLevel);
        return () => unregisterActiveNavLevel(currentNavLevel);
      }
      return undefined;
    }, [
      renderHeader,
      currentNavLevel,
      registerActiveNavLevel,
      unregisterActiveNavLevel,
    ]);

    const backRef =
      props.selectedDetail &&
      currentNavLevel !== undefined &&
      currentNavLevel === topNavLevel
        ? nth(currentNavLevel, backButtonReferences) || null
        : null;
    return (
      <>
        <AssociationObjectListMaster
          associationObjects={associationObjects}
          updateAssociation={updateAssociation}
          data={data}
          {...props}
        />
        {association && (
          <BusinessDataObjectDetailHeader
            collectionName={association.association.AssociatedCollectionName}
            id={association.id}
            headerRef={headerRef}
            onClick={
              currentNavLevel !== topNavLevel
                ? () =>
                    updateAssociation(
                      association.id,
                      association.association,
                      props.selectDetail
                    )
                : undefined
            }
          />
        )}
        {backRef &&
          association &&
          formContext &&
          createPortal(
            <EDIconButton
              icon="arrow_back"
              color="inherit"
              tooltip={`Tillbaka till ${formContext.model.RootType.SBLabel}`}
              padding="dense"
              marginTop={0.5}
              marginBottom={0.5}
              onClick={() => {
                unselectAssociation();
                props.selectDetail(null);
              }}
            />,
            backRef
          )}
      </>
    );
  };
};

export default MasterComp;
